// SPDX-License-Identifier: LicenseRef-Proprietary

import { logActivity } from '../components/VaccineDashboard/lib/storage';
import type { TreeNode as StoreTreeNode, TreeEdge } from './validation';

export type NodeType = "decision" | "chance" | "terminal";

export interface TreeNode {
  id: string;
  type: NodeType;
  label: string;
  nodeType?: 'healthy' | 'infection' | 'hospitalization' | 'death' | 'recover' | 'survive' | 'other'; // Epidemiological outcome type
  p?: number;
  cost_eur?: number;
  qaly?: number;
  notes?: string;
  transition_cost?: number; // Transition cost for this edge (from parent to this node)
  transition_utility_loss?: number; // Utility loss for this transition
  children?: TreeNode[];
}

export interface DecisionTree {
  id: string;
  title: string;
  root: TreeNode;
}

export interface LayoutNode extends TreeNode {
  x: number;
  y: number;
  width: number;
  height: number;
  level: number;
}

export type LegacyNodeType = NodeType;
export type LegacyTreeNode = TreeNode;
export type LegacyDecisionTree = DecisionTree;
export type LegacyLayoutNode = LayoutNode;

export interface ValidationIssue {
  nodeId: string;
  type: 'probability_sum' | 'missing_children' | 'invalid_range' | 'invalid_probability' | 'sharedChildCloned';
  message: string;
}

export interface ValidationResult {
  status: 'ok' | 'warning' | 'error';
  issues: ValidationIssue[];
}

// Tree manipulation utilities
export const createNode = (type: LegacyNodeType, label: string, parentId?: string): LegacyTreeNode => ({
  id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  type,
  label,
  children: type === 'terminal' ? undefined : []
});

export const addChild = (tree: LegacyDecisionTree, parentId: string, child: LegacyTreeNode): LegacyDecisionTree => {
  const addToNode = (node: LegacyTreeNode): LegacyTreeNode => {
    if (node.id === parentId) {
      logActivity(`Added child node ${child.id} to parent ${parentId}`);
      return {
        ...node,
        children: [...(node.children || []), child]
      };
    }
    if (node.children) {
      return {
        ...node,
        children: node.children.map(addToNode)
      };
    }
    return node;
  };

  return {
    ...tree,
    root: addToNode(tree.root)
  };
};

export const removeNode = (tree: LegacyDecisionTree, nodeId: string): LegacyDecisionTree => {
  const removeFromNode = (node: LegacyTreeNode): LegacyTreeNode | null => {
    if (node.id === nodeId) {
      logActivity(`Removed node: ${nodeId}`);
      return null;
    }
    if (node.children) {
      return {
        ...node,
        children: node.children.map(removeFromNode).filter(Boolean) as LegacyTreeNode[]
      };
    }
    return node;
  };

  const newRoot = removeFromNode(tree.root);
  return newRoot ? { ...tree, root: newRoot } : tree;
};

export const updateNode = (tree: LegacyDecisionTree, nodeId: string, updates: Partial<LegacyTreeNode>): LegacyDecisionTree => {
  const updateInNode = (node: LegacyTreeNode): LegacyTreeNode => {
    if (node.id === nodeId) {
      logActivity(`Updated node: ${nodeId}` + JSON.stringify(updates));
      return { ...node, ...updates };
    }
    if (node.children) {
      return {
        ...node,
        children: node.children.map(updateInNode)
      };
    }
    return node;
  };

  return {
    ...tree,
    root: updateInNode(tree.root)
  };
};

export const findNode = (tree: LegacyDecisionTree, nodeId: string): LegacyTreeNode | null => {
  const search = (node: LegacyTreeNode): LegacyTreeNode | null => {
    if (node.id === nodeId) return node;
    if (node.children) {
      for (const child of node.children) {
        const found = search(child);
        if (found) return found;
      }
    }
    return null;
  };
  return search(tree.root);
};

// Validation utilities
export const validateTree = (tree: LegacyDecisionTree): ValidationResult => {
  const issues: ValidationIssue[] = [];

  const validateNode = (node: LegacyTreeNode, path: string[] = []) => {
    // Check probability ranges
    if (node.p !== undefined && (node.p < 0 || node.p > 1)) {
      issues.push({
        nodeId: node.id,
        type: 'invalid_probability',
        message: `Probability ${node.p} is outside valid range [0,1]`
      });
    }

    // Check node type constraints
    if (node.type === 'decision' && (!node.children || node.children.length < 2)) {
      issues.push({
        nodeId: node.id,
        type: 'missing_children',
        message: 'Decision nodes must have at least 2 children'
      });
    }

    if (node.type === 'terminal' && node.children && node.children.length > 0) {
      issues.push({
        nodeId: node.id,
        type: 'missing_children',
        message: 'Terminal nodes should not have children'
      });
    }

    // Check probability sum for chance nodes
    if (node.type === 'chance' && node.children && node.children.length > 0) {
      const probabilities = node.children.map(child => child.p || 0);
      const sum = probabilities.reduce((a, b) => a + b, 0);
      const tolerance = 1e-6;
      
      if (Math.abs(sum - 1.0) > tolerance) {
        issues.push({
          nodeId: node.id,
          type: 'probability_sum',
          message: `Chance node probabilities sum to ${sum.toFixed(4)}, should be 1.0`
        });
      }
    }

    // Recursively validate children
    if (node.children) {
      node.children.forEach(child => validateNode(child, [...path, node.id]));
    }
  };

  validateNode(tree.root);

  const status = issues.some(i => i.type === 'probability_sum' || i.type === 'invalid_probability') 
    ? 'error' 
    : issues.length > 0 
    ? 'warning' 
    : 'ok';

  return { status, issues };
};

// Probability helpers
export const sumChildrenP = (node: LegacyTreeNode): number => {
  if (!node.children || node.children.length === 0) return 0;
  return node.children.reduce((acc, c) => acc + (c.p || 0), 0);
};

export const validateChance = (
  node: LegacyTreeNode,
  opts: { tol: number; amber: number } = { tol: 1e-6, amber: 0.01 }
): { status: 'ok' | 'warning' | 'error'; sum: number; delta: number } => {
  const sum = sumChildrenP(node);
  const delta = Math.abs(sum - 1.0);
  if (delta <= opts.tol) return { status: 'ok', sum, delta };
  if (delta <= opts.amber) return { status: 'warning', sum, delta };
  return { status: 'error', sum, delta };
};

export const autoNormalise = (node: LegacyTreeNode): LegacyTreeNode => {
  if (!node.children || node.children.length === 0) return node;
  const sum = sumChildrenP(node);
  if (sum <= 0) return node;
  const children = node.children.map(c => ({ ...c, p: (c.p || 0) / sum }));
  return { ...node, children };
};

// Layout calculation
export const calculateLayout = (tree: LegacyDecisionTree): LegacyLayoutNode[] => {
  const nodes: LegacyLayoutNode[] = [];
  const levelWidths: number[] = [];
  const nodeWidth = 120;
  const nodeHeight = 60;
  const levelSpacing = 200;
  const siblingSpacing = 40;

  const traverse = (node: LegacyTreeNode, level: number = 0, x: number = 0, y: number = 0) => {
    // Calculate level width
    if (!levelWidths[level]) levelWidths[level] = 0;
    levelWidths[level] = Math.max(levelWidths[level], x + nodeWidth);

    const layoutNode: LegacyLayoutNode = {
      ...node,
      x,
      y: level * levelSpacing,
      width: nodeWidth,
      height: nodeHeight,
      level
    };
    nodes.push(layoutNode);

    if (node.children && node.children.length > 0) {
      const childSpacing = Math.max(nodeWidth + siblingSpacing, (nodeWidth + siblingSpacing) * node.children.length / node.children.length);
      let childX = x - (node.children.length - 1) * childSpacing / 2;

      node.children.forEach((child, index) => {
        traverse(child, level + 1, childX, y);
        childX += childSpacing;
      });
    }
  };

  traverse(tree.root);

  // Center the tree
  const maxLevelWidth = Math.max(...levelWidths);
  const offsetX = (maxLevelWidth - levelWidths[0]) / 2;
  
  return nodes.map(node => ({
    ...node,
    x: node.x + offsetX
  }));
};

// Layered top-down layout (reduced edge crossings)
export const layoutTreeLayered = (
  root: LegacyTreeNode,
  opts: { hGap?: number; vGap?: number; nodeWidth?: number; nodeHeight?: number } = {}
): LayoutNode[] => {
  const hGap = opts.hGap ?? 40;
  const vGap = opts.vGap ?? 120;
  const nodeWidth = opts.nodeWidth ?? 140;
  const nodeHeight = opts.nodeHeight ?? 60;

  // Clone map to avoid shared children (enforce tree)
  const seen = new Map<string, number>();
  const cloneIfShared = (n: LegacyTreeNode): LegacyTreeNode => {
    const count = (seen.get(n.id) || 0) + 1;
    seen.set(n.id, count);
    const base: TreeNode = {
      ...n,
      id: count > 1 ? `${n.id}_clone_${count}` : n.id
    };
    if (n.children && n.children.length > 0) {
      // sort children deterministically by label to reduce crossings
      const sorted = [...n.children].sort((a, b) => a.label.localeCompare(b.label));
      base.children = sorted.map(cloneIfShared);
    }
    return base;
  };

  const cleanRoot = cloneIfShared(root);

  // compute subtree widths
  const subtreeWidth = new Map<string, number>();
  const measure = (n: LegacyTreeNode): number => {
    if (!n.children || n.children.length === 0) {
      subtreeWidth.set(n.id, nodeWidth);
      return nodeWidth;
    }
    const widths = n.children.map(measure);
    const total = widths.reduce((a, b) => a + b, 0) + hGap * (n.children.length - 1);
    subtreeWidth.set(n.id, Math.max(total, nodeWidth));
    return subtreeWidth.get(n.id)!;
  };
  measure(cleanRoot);

  const nodes: LegacyLayoutNode[] = [];
  const place = (n: LegacyTreeNode, depth: number, left: number) => {
    const width = subtreeWidth.get(n.id)!;
    const xCenter = left + width / 2 - nodeWidth / 2;
    const y = depth * (nodeHeight + vGap);
    nodes.push({ ...n, x: xCenter, y, width: nodeWidth, height: nodeHeight, level: depth });
    if (n.children && n.children.length > 0) {
      let cursor = left;
      for (const child of n.children) {
        const cw = subtreeWidth.get(child.id)!;
        place(child, depth + 1, cursor);
        cursor += cw + hGap;
      }
    }
  };
  place(cleanRoot, 0, 0);
  return nodes;
};

// Path analysis
export const calculatePathProbability = (tree: LegacyDecisionTree, terminalNodeId: string): number => {
  const path: string[] = [];
  
  const findPath = (node: LegacyTreeNode, targetId: string, currentPath: string[] = []): boolean => {
    const newPath = [...currentPath, node.id];
    
    if (node.id === targetId) {
      path.push(...newPath);
      return true;
    }
    
    if (node.children) {
      for (const child of node.children) {
        if (findPath(child, targetId, newPath)) {
          return true;
        }
      }
    }
    
    return false;
  };

  if (!findPath(tree.root, terminalNodeId)) {
    return 0;
  }

  let probability = 1;
  for (let i = 0; i < path.length - 1; i++) {
    const node = findNode(tree, path[i]);
    const nextNode = findNode(tree, path[i + 1]);
    
    if (node && nextNode && node.children) {
      const childNode = node.children.find(child => child.id === nextNode.id);
      if (childNode && childNode.p !== undefined) {
        probability *= childNode.p;
      }
    }
  }

  return probability;
};

// Default tree creation
export const createBlankTree = (): DecisionTree => ({
  id: `tree_${Date.now()}`,
  title: 'New Decision Tree',
  root: createNode('decision', 'Decision')
});

/**
 * Creates a simple blank slate decision tree: Vaccinate → No infection / Infection
 * Users can expand by adding more outcomes to each branch
 */
export function createInfluenzaBlankSlate(
  treatmentCost: number = 600
): { nodes: Record<string, StoreTreeNode>; edges: TreeEdge[]; rootId: string } {
  const nodes: Record<string, StoreTreeNode> = {};
  const edges: TreeEdge[] = [];
  
  // Root decision node: Vaccinate?
  const rootId = 'root_decision';
  nodes[rootId] = {
    id: rootId,
    type: 'decision',
    label: 'Vaccinate?',
    children: ['vaccinate_chance', 'no_vaccinate_chance']
  };
  
  // Vaccinate branch
  const vaccinateChanceId = 'vaccinate_chance';
  nodes[vaccinateChanceId] = {
    id: vaccinateChanceId,
    type: 'chance',
    label: 'Vaccinate',
    children: ['vac_no_infection', 'vac_infection']
  };
  edges.push({
    parentId: rootId,
    childId: vaccinateChanceId,
    label: 'Vaccinate'
  });
  
  // No vaccinate branch
  const noVaccinateChanceId = 'no_vaccinate_chance';
  nodes[noVaccinateChanceId] = {
    id: noVaccinateChanceId,
    type: 'chance',
    label: 'No vaccinate',
    children: ['no_vac_no_infection', 'no_vac_infection']
  };
  edges.push({
    parentId: rootId,
    childId: noVaccinateChanceId,
    label: 'No vaccinate'
  });
  
  // Vaccinate outcomes
  nodes['vac_no_infection'] = {
    id: 'vac_no_infection',
    type: 'terminal',
    label: 'No infection',
    cost: 0, // No treatment needed (programme cost added by backend)
    qaly: 1.0, // Perfect health
    children: []
  };
  edges.push({
    parentId: vaccinateChanceId,
    childId: 'vac_no_infection',
    probability: 0.95,
    label: 'No infection'
  });
  
  nodes['vac_infection'] = {
    id: 'vac_infection',
    type: 'terminal',
    label: 'Infection',
    cost: treatmentCost, // Treatment cost only (no programme cost here)
    qaly: 0.90, // Milder infection due to vaccination
    children: []
  };
  edges.push({
    parentId: vaccinateChanceId,
    childId: 'vac_infection',
    probability: 0.05,
    label: 'Infection'
  });
  
  // No vaccinate outcomes
  nodes['no_vac_no_infection'] = {
    id: 'no_vac_no_infection',
    type: 'terminal',
    label: 'No infection',
    cost: 0, // No treatment needed
    qaly: 1.0, // Perfect health
    children: []
  };
  edges.push({
    parentId: noVaccinateChanceId,
    childId: 'no_vac_no_infection',
    probability: 0.80,
    label: 'No infection'
  });
  
  nodes['no_vac_infection'] = {
    id: 'no_vac_infection',
    type: 'terminal',
    label: 'Infection',
    cost: treatmentCost, // Treatment cost only
    qaly: 0.80, // More severe infection without vaccination
    children: []
  };
  edges.push({
    parentId: noVaccinateChanceId,
    childId: 'no_vac_infection',
    probability: 0.20,
    label: 'Infection'
  });
  
  return { nodes, edges, rootId };
}
